import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Phone } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { getCalApi } from "@calcom/embed-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const Contact = () => {
  usePageMeta(
    "Contact Us — Shattara AI",
    "Get in touch with the Shattara AI team. We'd love to hear from you — whether you have questions, feedback, or partnership inquiries."
  );
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    feedbackType: "",
    message: "",
  });

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "default" });
      cal("ui", {
        cssVarsPerTheme: {
          light: { "cal-brand": "#007ee5" },
          dark: { "cal-brand": "#fafafa" },
        },
        hideEventTypeDetails: false,
        layout: "month_view",
      });
    })();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    toast.success("Message sent successfully!");
    console.log(formData);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="container mx-auto px-4 py-12 sm:py-20">
        <div className="max-w-2xl mx-auto space-y-10">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">Contact the Shattara AI team</h1>

            <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
              We'd love to hear from you. Send us a message and we'll get back to you as soon as possible.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Name + Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-11 rounded-lg"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-11 rounded-lg"
                  required
                />
              </div>
            </div>

            {/* Feedback */}
            <div className="space-y-2">
              <Label htmlFor="feedbackType">Feedback type</Label>

              <Select
                value={formData.feedbackType}
                onValueChange={(value) => setFormData({ ...formData, feedbackType: value })}
              >
                <SelectTrigger className="h-11 rounded-lg">
                  <SelectValue placeholder="Select feedback type" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="struggling">😫 I'm struggling with this in Shattara AI...</SelectItem>

                  <SelectItem value="love">😍 I love this about Shattara AI...</SelectItem>

                  <SelectItem value="feature">😇 I wish Shattara AI had this feature...</SelectItem>

                  <SelectItem value="bug">😵‍💫 I found a bug in Shattara AI...</SelectItem>

                  <SelectItem value="thoughts">🤔 My overall thoughts on Shattara AI...</SelectItem>

                  <SelectItem value="other">💬 Something else...</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>

              <Textarea
                id="message"
                placeholder="Tell us what's on your mind..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="min-h-40 rounded-lg resize-y"
                required
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3 pt-2">
              <Button
                type="submit"
                className="w-full h-11 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
              >
                Send Message
                <Send className="w-4 h-4 ml-2" />
              </Button>

              <Button
                type="button"
                variant="outline"
                data-cal-namespace="default"
                data-cal-link="shattaraai/product-walkthrough"
                data-cal-origin="https://cal.id"
                data-cal-config='{"layout":"month_view"}'
                className="w-full h-11 rounded-lg font-medium"
              >
                Schedule a call
                <Phone className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
